import moment from 'moment';
import { DynamicObjectInterface } from '../../sdk/type';
import { REACT_APP_LANDING_PAGE, REACT_APP_API_BASEURL, REACT_APP_ENV } from '../../envVars';
import { CampaignProgressStatus, Country, EnrollmentType, Gender, RewardStatus, Status } from '../components';

export const getAPIVersion = () => {
  return localStorage.getItem('appVersion') ?? '';
};
export const getEmplId = () => localStorage.getItem('EmplId') ?? '';

export const getAPIHost = () => {
  let baseUrl = REACT_APP_API_BASEURL ?? `${window.location.origin}`;

  // Remove trailing slash if present
  baseUrl = baseUrl.replace(/\/$/, '');

  const localDomains = ['localhost'];
  const currentDomain = window.location.hostname;

  if (!localDomains.includes(currentDomain)) {
    return `${baseUrl}/api`;
  }
  return baseUrl;
};

export const getLandingPage = () => REACT_APP_LANDING_PAGE;
export const getAppToken = () => localStorage.getItem('appToken') ?? '';
export const getLoginToken = () => localStorage.getItem('loginToken') ?? '';
export const getIdToken = () => localStorage.getItem('idToken') ?? '';
export const getAppLanguage = () => localStorage.getItem('locale') ?? '';
export const getAppCountry = () => localStorage.getItem('country') ?? '';

export const apiHeaders = () => {
  return {
    headers: {
      'x-access-token': getAppToken(),
      'x-app-token': getLoginToken(),
      'x-app-country': getAppCountry(),
      'x-app-language': getAppLanguage(),
      'Content-Type': 'application/json',
    },
  };
};

export const bodyParams = () => {
  return {
    idToken: localStorage.getItem('idToken'),
    accessToken: localStorage.getItem('accessToken'),
  };
};

export const getEnvironment = (): string => (REACT_APP_ENV === 'PROD' ? '' : REACT_APP_ENV)?.toUpperCase() ?? '';

export const formatNumber = (number: DynamicObjectInterface, decimal: number, prefix: string, postfix: string) => {
  if (isNaN(number)) number = 0;
  number = parseFloat(number);
  if (!prefix) prefix = '';
  if (!postfix) postfix = '';
  return (
    prefix +
    number.toLocaleString(undefined, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    }) +
    postfix
  );
};

export const getMonthName = (number: number, abbreviate = false) => {
  let months = [
    { name: 'January', sf: 'Jan' },
    { name: 'February', sf: 'Feb' },
    { name: 'March', sf: 'Mar' },
    { name: 'April', sf: 'Apr' },
    { name: 'May', sf: 'May' },
    { name: 'June', sf: 'June' },
    { name: 'July', sf: 'July' },
    { name: 'August', sf: 'Aug' },
    { name: 'September', sf: 'Sept' },
    { name: 'October', sf: 'Oct' },
    { name: 'November', sf: 'Nov' },
    { name: 'December', sf: 'Dec' },
  ];
  return abbreviate ? months[number - 1].sf : months[number - 1].name;
};

export const getDayName = (number: number) => {
  let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[number];
};

export const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');

export const getQSParam = (key: string) => {
  let qs = new URLSearchParams(window.location.search);
  return qs.get(key);
};

export const shortNumber = (n: DynamicObjectInterface, d: number) => {
  let num: number = parseFloat(n);

  if (num < 1000) return parseFloat(n).toFixed(d);
  else if (n < 1000000) return (num / 1000).toFixed(d) + 'K';
  else return (num / 1000000).toFixed(d) + 'M';
};

export const sort = (key: string, order = 'asc') => {
  return function innerSort(a: DynamicObjectInterface, b: DynamicObjectInterface) {
    if (!a.key || !b.key) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key] || '';
    const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key] || '';

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === 'desc' ? comparison * -1 : comparison;
  };
};

export const dateYYYYMMDD = (str: string) => {
  let s = str.substring(0, 10).split('-', 3);
  return new Date(parseInt(s[0]), parseInt(s[1]) - 1, parseInt(s[2]), 0, 0, 0, 0);
};

export const formatDate = (str: string) => {
  // from YYYY-MM-DD
  if (str === '' || str === null || str === undefined) return '';
  else if (str.indexOf('T') > 0 || str.indexOf(' ') > 0) {
    // for date with timezone
    let d = new Date(str);
    return zeroPad(d.getMonth() + 1, 2) + '/' + zeroPad(d.getDate(), 2) + '/' + d.getFullYear(); // return mm/dd/yyyy
  } else {
    // for date only
    let s = str.split('-');
    return s[1] + '/' + s[2] + '/' + s[0]; // return mm/dd/yyyy
  }
};

export const getTimeforDate = (str: string) => {
  if (str === '' || str === null || str === undefined) return '';
  else if (str.indexOf('T') > 0 || str.indexOf(' ') > 0) {
    let today = new Date(str),
      time = today.toLocaleString('en-US', { hour: 'numeric', hour12: true });
    return time;
  }
};

export const arrayRemoveNull = (array: Object[]) => {
  let newArray = array.filter(item => {
    return item;
  });
  return newArray;
};

export const isEmpty = (obj: object) => {
  return Object.keys(obj).length === 0;
};

export const phoneNumberFormat = (str: string) => {
  if (str.indexOf('-') === 3 && str.indexOf('-', 4) === 7) return str;
  else {
    let cleaned = ('' + str).replace(/\D/g, '');
    let match = RegExp(/^(\d{3})(\d{3})(\d{4})$/).exec(cleaned);
    if (match) {
      return match[1] + '-' + match[2] + '-' + match[3];
    }
    return null;
  }
};

export const removeSpecialChar = (text: string) => {
  return text.replace(/[^a-zA-Z0-9]/g, '');
};

export const checkIsDuplicate = <T>(objList: T[], arg: { anchor?: keyof T; target: keyof T; value: T }): boolean => {
  let isDuplicate = false;
  objList.forEach(obj => {
    if (obj[arg.target] === arg.value[arg.target]) {
      isDuplicate = true;
      if (arg.anchor) {
        if (obj[arg.anchor] === arg.value[arg.anchor]) {
          isDuplicate = false;
        }
      }
    }
  });
  return isDuplicate;
};

export const randomStr = (len = 21) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < len; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const getAboId = () => {
  return localStorage.getItem('aboId');
};

export const convertUTCdate = (date: string): string => {
  // Parse the date and subtract 8 hours to convert to UTC time from GMT+8
  return moment(date, 'YYYY-MM-DD HH:mm:ss').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

export const convertGMT8date = (date: string): string => {
  // Parse the date and add 8 hours to convert to GMT+8 from UTC
  return moment(new Date(new Date(date + 'Z'))).format('YYYY-MM-DDTHH:mm:ss.SSS');
};

// export const convertGMT8date = (date: string): string => {
//   return moment.parseZone(date)
//     .local()
//     .format('YYYY-MM-DDTHH:mm:ss.SSS');
// };

export const convertLocalDate = (date: string, formatStr: string = 'YYYY-MM-DD'): string => {
  return moment.parseZone(date)
    .local()
    .format(formatStr);
};

/**
 * Converts any input string to title case.
 * Trims whitespace, lowercases all letters, then capitalizes the first character.
 * If input is empty or undefined, returns a fallback (default: '-').
 *
 * @param input - The string to format (e.g., 'fEMaLE', ' nationality ')
 * @param fallback - Value to return if input is missing or falsy
 * @returns Formatted string in Title Case or fallback value
 */
export function formatTitleCase(input?: string, fallback: string = '-'): string {
  if (!input || typeof input !== 'string') return fallback;

  const normalized = input.trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Translates a normalized string value using a mapping and i18n function.
 *
 * @param translation i18n translation function
 * @param value input string to translate
 * @param map object mapping normalized values to i18n keys
 * @param fallback value to return if no match is found (default: '-')
 * @returns translated string or fallback
 */
export function translateEnumValue(
  translation: Function,
  value: string,
  map: Record<string, string>,
  fallback: string = '-',
): string {
  if (!value || typeof value !== 'string') return fallback;

  const normalized = value.trim();
  const i18nKey = map[normalized];

  return i18nKey ? translation(i18nKey) : fallback;
}

export function getCampaignProgressStatus(translation: Function, status: string = '', fallback: string = '-'): string {
  return translateEnumValue(
    translation,
    status,
    {
      [CampaignProgressStatus.COMPLETED]: 'dashboard.profile.label.completed',
      [CampaignProgressStatus.NOT_COMPLETED]: 'dashboard.profile.label.notCompleted',
    },
    fallback,
  );
}

export function getGender(translation: Function, gender: string = '', fallback: string = '-'): string {
  return translateEnumValue(
    translation,
    gender.toUpperCase(),
    {
      [Gender.MALE]: 'dashboard.profile.label.male',
      [Gender.FEMALE]: 'dashboard.profile.label.female',
    },
    fallback,
  );
}

export function getEnrollmentType(translation: Function, role: string = '', fallback: string = '-'): string {
  return translateEnumValue(
    translation,
    role.toUpperCase(),
    {
      [EnrollmentType.COACH]: 'dashboard.my-progress.enrollmentType.coach',
      [EnrollmentType.LEADER]: 'dashboard.my-progress.enrollmentType.leader',
      [EnrollmentType.PARTICIPANT]: 'dashboard.my-progress.enrollmentType.participant',
    },
    fallback,
  );
}

export function getTeamStatus(translation: Function, status: string = '', fallback: string = '-'): string {
  return translateEnumValue(
    translation,
    status.toUpperCase(),
    {
      [Status.ACTIVE]: 'dashboard.global.status.active',
      [Status.INACTIVE]: 'dashboard.global.status.inactive',
    },
    fallback,
  );
}

export function getRewardStatus(translation: Function, status: string = '', fallback: string = '-'): string {
  return translateEnumValue(
    translation,
    status.toUpperCase(),
    {
      [RewardStatus.ALLOCATED]: 'dashboard.global.rewardStatus.allocated',
      [RewardStatus.DISTRIBUTED]: 'dashboard.global.rewardStatus.distributed',
      [RewardStatus.SELECTED]: 'dashboard.global.rewardStatus.selected',
    },
    fallback,
  );
}

/**
 * Returns a locale string based on country code in localStorage.
 * Supports "MY" (Malaysia) and "ID" (Indonesia), with "en" as fallback.
 */
export function getCountryLocale(): string {
  const countryCode = getAppCountry();

  switch (countryCode) {
    case Country.MY:
      return 'en';
    case Country.IDN:
      return 'id'; // Indonesian
    default:
      return 'en'; // Fallback
  }
}

export const sanitizeOrderNumber = (input?: string): string | undefined => {
  if (!input) return undefined;

  const orders = input
    .split(',')
    .map(order => order.trim())
    .filter(Boolean);

  const uniqueOrders = Array.from(new Set(orders));

  return uniqueOrders.join(',');
};
